// components/Pulsar.js
//
// The pulsar@ intake agent's window into ERAAI.
//
// The agent runs as its own process next to the API: it polls the pulsar@
// mailbox, creates a project per incoming questionnaire, and stages the
// generated export on disk for a human to check. This page is that human's
// workspace -- see what the agent is doing, download what it produced, and send
// a corrected version back to the client.
//
// Read-mostly: nothing here starts, stops or reconfigures the agent. The single
// write is uploading a corrected file, which drops it into the agent's
// readytogo/ folder -- and the agent emails whatever it finds there to the
// client within ~30 seconds. That makes the upload button a send button, so it
// goes through a confirmation naming the recipient and the thread first.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles/Pulsar.css';
import apiService from '../services/apiService';
import {
  FaSyncAlt, FaDownload, FaUpload, FaCircle, FaExclamationTriangle,
  FaPaperPlane, FaTimes, FaFileAlt, FaInbox, FaSpinner, FaSearch,
} from 'react-icons/fa';

// The log panel polls rather than holding a socket open. The agent writes to a
// file on a 30s sweep and a 10-minute poll, so a few seconds of latency is
// invisible, and this needs no new socket.io namespace to go wrong.
const LOG_POLL_MS = 5000;
const JOBS_POLL_MS = 15000;

const STATUS_LABELS = {
  awaiting_review: { label: 'Awaiting review', className: 'pulsar-badge-review' },
  queued: { label: 'Queued to send', className: 'pulsar-badge-queued' },
  sent: { label: 'Sent to client', className: 'pulsar-badge-sent' },
  export_pending: { label: 'Export needed by hand', className: 'pulsar-badge-pending' },
  empty: { label: 'Nothing staged', className: 'pulsar-badge-empty' },
};

const LEVEL_CLASSES = {
  ERROR: 'pulsar-log-error',
  CRITICAL: 'pulsar-log-error',
  WARNING: 'pulsar-log-warning',
  INFO: 'pulsar-log-info',
  DEBUG: 'pulsar-log-debug',
};

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function Pulsar() {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [autoScroll, setAutoScroll] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [toast, setToast] = useState(null);
  const [confirmUpload, setConfirmUpload] = useState(null);

  const logEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingJobRef = useRef(null);

  // ── Data loading ──────────────────────────────────────────────────────

  const loadJobs = useCallback(async ({ quiet = false } = {}) => {
    try {
      const [jobsRes, statusRes] = await Promise.all([
        apiService.get('/api/pulsar/jobs'),
        apiService.get('/api/pulsar/status'),
      ]);
      setJobs(jobsRes.data?.jobs || []);
      setStatus(statusRes.data || null);
      setError('');
    } catch (e) {
      // A failed background refresh must not wipe the table the reviewer is
      // reading -- only a failed first load is worth an error screen.
      if (!quiet) setError(e.message || 'Could not reach the agent');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await apiService.get('/api/pulsar/logs?limit=500');
      setLogEntries(res.data?.entries || []);
    } catch {
      // Silent: the log panel is secondary, and a blip here shouldn't throw an
      // error banner over a page that is otherwise working.
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadLogs();
  }, [loadJobs, loadLogs]);

  useEffect(() => {
    const jobsTimer = setInterval(() => loadJobs({ quiet: true }), JOBS_POLL_MS);
    const logsTimer = setInterval(loadLogs, LOG_POLL_MS);
    return () => {
      clearInterval(jobsTimer);
      clearInterval(logsTimer);
    };
  }, [loadJobs, loadLogs]);

  useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ block: 'end' });
    }
  }, [logEntries, autoScroll]);

  // ── Actions ───────────────────────────────────────────────────────────

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 6000);
  };

  const downloadFile = async (job, file, folder = 'review') => {
    const key = `${job.client}/${job.project}/${file.name}`;
    setBusyKey(key);
    try {
      const params = new URLSearchParams({
        folder,
        client: job.client,
        project: job.project,
        filename: file.name,
      });
      await apiService.download(`/api/pulsar/file?${params}`, file.name);
    } catch (e) {
      showToast('error', e.message || 'Download failed');
    } finally {
      setBusyKey('');
    }
  };

  // Picking a file does not send it. The chosen file is held here and the
  // confirmation dialog is what actually calls the API, because dropping it
  // into readytogo/ is what makes the agent email the client.
  const pickCorrectedFile = (job) => {
    pendingJobRef.current = job;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onFileChosen = (event) => {
    const file = event.target.files?.[0];
    const job = pendingJobRef.current;
    if (!file || !job) return;

    const maxBytes = status?.max_upload_bytes || 3 * 1048576;
    if (file.size > maxBytes) {
      showToast('error',
        `${file.name} is ${formatBytes(file.size)}. The agent cannot email anything `
        + `over ${formatBytes(maxBytes)}, so it would never be sent.`);
      return;
    }
    setConfirmUpload({ job, file });
  };

  const sendCorrectedFile = async () => {
    if (!confirmUpload) return;
    const { job, file } = confirmUpload;
    const key = `${job.client}/${job.project}/upload`;
    setBusyKey(key);
    try {
      const formData = new FormData();
      formData.append('client', job.client);
      formData.append('project', job.project);
      formData.append('file', file);
      const res = await apiService.upload('/api/pulsar/readytogo', formData);
      const to = res.data?.will_email || job.sender;
      showToast('success',
        `${res.data?.filename} is queued. The agent will reply to ${to} within a minute.`);
      setConfirmUpload(null);
      loadJobs({ quiet: true });
    } catch (e) {
      showToast('error', e.message || 'Upload failed');
    } finally {
      setBusyKey('');
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const needle = search.trim().toLowerCase();
  const visibleJobs = needle
    ? jobs.filter((job) => [job.client, job.project, job.sender, job.subject, job.project_id]
        .some((field) => String(field || '').toLowerCase().includes(needle)))
    : jobs;

  const visibleLogs = levelFilter === 'ALL'
    ? logEntries
    : logEntries.filter((entry) => entry.level === levelFilter);

  const errorCount = logEntries.filter(
    (entry) => entry.level === 'ERROR' || entry.level === 'CRITICAL').length;

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="pulsar-page pulsar-centered">
        <FaSpinner className="pulsar-spin" />
        <p>Loading the agent's state…</p>
      </div>
    );
  }

  return (
    <div className="pulsar-page">
      <header className="pulsar-header">
        <div>
          <h1>Pulsar</h1>
          <p className="pulsar-subtitle">
            The pulsar@ intake agent — projects created from client email, staged here
            for review before anything goes back out.
          </p>
        </div>
        <div className="pulsar-header-actions">
          <span className={`pulsar-liveness ${status?.stale ? 'stale' : 'live'}`}>
            <FaCircle />
            {status?.stale ? 'No activity in 30+ min' : 'Agent active'}
            {status?.log_modified && (
              <small> · last log {formatWhen(status.log_modified)}</small>
            )}
          </span>
          <button className="pulsar-btn pulsar-btn-ghost"
                  onClick={() => { loadJobs(); loadLogs(); }}>
            <FaSyncAlt /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="pulsar-banner pulsar-banner-error">
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {status && !status.dir_present && (
        <div className="pulsar-banner pulsar-banner-error">
          <FaExclamationTriangle />
          The agent folder isn't where the API expects it ({status.pulsar_dir}).
          Set PULSAR_DIR on the API host.
        </div>
      )}

      <section className="pulsar-stats">
        <div className="pulsar-stat">
          <span className="pulsar-stat-value">{status?.review_projects ?? 0}</span>
          <span className="pulsar-stat-label">Projects staged</span>
        </div>
        <div className="pulsar-stat">
          <span className="pulsar-stat-value">{status?.queued_projects ?? 0}</span>
          <span className="pulsar-stat-label">Queued to send</span>
        </div>
        <div className="pulsar-stat">
          <span className="pulsar-stat-value">{status?.sent_projects ?? 0}</span>
          <span className="pulsar-stat-label">Sent</span>
        </div>
        <div className={`pulsar-stat ${errorCount ? 'pulsar-stat-alert' : ''}`}>
          <span className="pulsar-stat-value">{errorCount}</span>
          <span className="pulsar-stat-label">Errors in recent log</span>
        </div>
      </section>

      <section className="pulsar-section">
        <div className="pulsar-section-head">
          <h2>Projects</h2>
          <div className="pulsar-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Filter by client, project, sender or id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {visibleJobs.length === 0 ? (
          <div className="pulsar-empty">
            <FaInbox />
            <p>{jobs.length === 0
              ? 'The agent hasn\'t staged any projects yet.'
              : 'No project matches that filter.'}</p>
          </div>
        ) : (
          <div className="pulsar-table-scroll">
            <table className="pulsar-table">
              <thead>
                <tr>
                  <th>Client / Project</th>
                  <th>From</th>
                  <th>Export</th>
                  <th>Status</th>
                  <th>Staged files</th>
                  <th>Corrected</th>
                </tr>
              </thead>
              <tbody>
                {visibleJobs.map((job) => {
                  const meta = STATUS_LABELS[job.status] || STATUS_LABELS.empty;
                  const uploadKey = `${job.client}/${job.project}/upload`;
                  return (
                    <tr key={`${job.client}/${job.project}`}>
                      <td>
                        <div className="pulsar-project">{job.project}</div>
                        <div className="pulsar-meta">
                          {job.client}
                          {job.project_id ? ` · id ${job.project_id}` : ''}
                          {job.email_count > 1 ? ` · ${job.email_count} emails` : ''}
                        </div>
                      </td>
                      <td>
                        <div>{job.sender || '—'}</div>
                        <div className="pulsar-meta" title={job.subject}>{job.subject || ''}</div>
                        <div className="pulsar-meta">{formatWhen(job.processed_at)}</div>
                      </td>
                      <td>
                        <div>{job.pipeline === 'dp' ? 'DP (.sav)' : (job.export_type || '—')}</div>
                        {job.export_pending && (
                          <div className="pulsar-meta pulsar-warn">generate by hand</div>
                        )}
                      </td>
                      <td>
                        <span className={`pulsar-badge ${meta.className}`}>{meta.label}</span>
                      </td>
                      <td>
                        {job.review_files.length === 0 ? (
                          <span className="pulsar-meta">nothing staged</span>
                        ) : (
                          <ul className="pulsar-files">
                            {job.review_files.map((file) => (
                              <li key={file.name}>
                                <button
                                  className="pulsar-file-btn"
                                  onClick={() => downloadFile(job, file)}
                                  disabled={busyKey === `${job.client}/${job.project}/${file.name}`}
                                  title={`Download ${file.name}`}
                                >
                                  {busyKey === `${job.client}/${job.project}/${file.name}`
                                    ? <FaSpinner className="pulsar-spin" />
                                    : <FaDownload />}
                                  <span className="pulsar-file-name">{file.name}</span>
                                  <span className="pulsar-meta">{formatBytes(file.size)}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td>
                        <button
                          className="pulsar-btn pulsar-btn-primary"
                          onClick={() => pickCorrectedFile(job)}
                          disabled={busyKey === uploadKey}
                          title="Upload the corrected file — this emails it to the client"
                        >
                          {busyKey === uploadKey
                            ? <FaSpinner className="pulsar-spin" />
                            : <FaUpload />} Upload & send
                        </button>
                        {job.queued_files.length > 0 && (
                          <div className="pulsar-meta pulsar-warn">
                            {job.queued_files.length} file(s) already queued
                          </div>
                        )}
                        {job.sent_files.length > 0 && (
                          <div className="pulsar-meta">
                            last sent {formatWhen(job.sent_files[0].modified)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="pulsar-section">
        <div className="pulsar-section-head">
          <h2>Agent log</h2>
          <div className="pulsar-log-controls">
            <label className="pulsar-checkbox">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
              />
              Follow
            </label>
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              <option value="ALL">All levels</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
            </select>
          </div>
        </div>

        <div className="pulsar-log">
          {visibleLogs.length === 0 ? (
            <div className="pulsar-empty">
              <FaFileAlt />
              <p>No log lines yet. The agent writes here on every pass.</p>
            </div>
          ) : (
            visibleLogs.map((entry, index) => (
              <div key={`${entry.timestamp}-${index}`}
                   className={`pulsar-log-line ${LEVEL_CLASSES[entry.level] || ''}`}>
                <span className="pulsar-log-ts">{entry.timestamp}</span>
                <span className="pulsar-log-level">{entry.level}</span>
                <span className="pulsar-log-msg">
                  {entry.message}
                  {entry.detail && <pre className="pulsar-log-detail">{entry.detail}</pre>}
                </span>
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </section>

      {/* Hidden input: the visible control is the per-row Upload button. */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChosen}
        style={{ display: 'none' }}
      />

      {confirmUpload && (
        <div className="pulsar-modal-overlay" onClick={() => setConfirmUpload(null)}>
          <div className="pulsar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pulsar-modal-head">
              <h3><FaPaperPlane /> Send this to the client?</h3>
              <button className="pulsar-modal-close"
                      onClick={() => setConfirmUpload(null)}><FaTimes /></button>
            </div>
            <div className="pulsar-modal-body">
              <p>
                The agent will reply into the original mail thread with this file
                attached, within about a minute. There is no undo once it has sent.
              </p>
              <dl className="pulsar-confirm-details">
                <dt>File</dt>
                <dd>{confirmUpload.file.name}
                  <span className="pulsar-meta"> ({formatBytes(confirmUpload.file.size)})</span></dd>
                <dt>Goes to</dt>
                <dd>{confirmUpload.job.sender || '(unknown sender)'}</dd>
                <dt>In reply to</dt>
                <dd>{confirmUpload.job.subject || '(no subject)'}</dd>
                <dt>Project</dt>
                <dd>{confirmUpload.job.client} / {confirmUpload.job.project}</dd>
              </dl>
              {confirmUpload.job.queued_files.length > 0 && (
                <div className="pulsar-banner pulsar-banner-warn">
                  <FaExclamationTriangle />
                  {confirmUpload.job.queued_files.length} file(s) are already queued for
                  this project. The agent sends every file it finds, so the client will
                  receive those too.
                </div>
              )}
            </div>
            <div className="pulsar-modal-foot">
              <button className="pulsar-btn pulsar-btn-ghost"
                      onClick={() => setConfirmUpload(null)}>Cancel</button>
              <button className="pulsar-btn pulsar-btn-primary"
                      onClick={sendCorrectedFile}
                      disabled={busyKey.endsWith('/upload')}>
                {busyKey.endsWith('/upload')
                  ? <><FaSpinner className="pulsar-spin" /> Sending…</>
                  : <><FaPaperPlane /> Yes, send it</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`pulsar-toast pulsar-toast-${toast.type}`}>
          {toast.type === 'error' ? <FaExclamationTriangle /> : <FaPaperPlane />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)}><FaTimes /></button>
        </div>
      )}
    </div>
  );
}

export default Pulsar;
